import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const urlFor = (teamId, kind, templateId) => {
  const resource = kind === 'offer' ? 'offer_templates' : 'budget_templates';
  return `/teams/${teamId}/${resource}/v3/${templateId}`;
};

// Fetches the selected template's #show on templateId change, and exposes that
// same fetch as `reload` so a caller (e.g. after saving in the edit drawer) can
// force a fresh read without changing templateId. Stamps each load with an id so
// an out-of-order response from a superseded load can never overwrite the
// current one (same guard as useV3BudgetTemplate).
const useTemplateSummary = ({ teamId, kind, templateId, csrfToken }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const loadRef = useRef(0);

  const load = useCallback(() => {
    // Bump loadRef so any in-flight request from a prior templateId is
    // invalidated and can't restore stale data after the selection clears.
    if (!templateId) { loadRef.current += 1; setData(null); setLoading(false); setError(false); return undefined; }

    const loadId = loadRef.current + 1;
    loadRef.current = loadId;
    setData(null);
    setLoading(true);
    setError(false);

    return axios.get(urlFor(teamId, kind, templateId), { headers: { 'X-CSRF-TOKEN': csrfToken } })
      .then((r) => { if (loadId === loadRef.current) setData(r.data); })
      .catch(() => { if (loadId === loadRef.current) setError(true); })
      .finally(() => { if (loadId === loadRef.current) setLoading(false); });
  }, [teamId, kind, templateId, csrfToken]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, reload: load };
};

export default useTemplateSummary;
